export interface ProfileData {
  name: string;
  email: string;
  jiraToken: string;
  jiraProject: string;
  jiraEmail?: string;
}

const PROFILE_STORAGE_KEY = 'userProfile';

export class ProfileService {
  /**
   * Get profile data from localStorage
   */
  static getProfile(): ProfileData | null {
    try {
      const data = localStorage.getItem(PROFILE_STORAGE_KEY);
      if (!data) return null;
      return JSON.parse(data);
    } catch (error) {
      console.error('Error reading profile from localStorage:', error);
      return null;
    }
  }

  /**
   * Save profile data to localStorage
   */
  static saveProfile(profile: ProfileData): boolean {
    try {
      localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
      return true;
    } catch (error) {
      console.error('Error saving profile to localStorage:', error);
      return false;
    }
  }

  /**
   * Clear profile data from localStorage
   */
  static clearProfile(): void {
    try {
      localStorage.removeItem(PROFILE_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing profile from localStorage:', error);
    }
  }

  /**
   * Get Jira credentials for API requests
   */
  static getJiraCredentials(): { email: string; token: string; project: string } | null {
    const profile = this.getProfile();
    const project = profile?.jiraProject?.trim() ?? '';
    const email = (profile?.jiraEmail || profile?.email || '').trim();
    if (!profile || !profile.jiraToken || !email || !project) {
      return null;
    }

    return {
      email,
      token: profile.jiraToken,
      project,
    };
  }

  /**
   * Check if profile is complete
   */
  static isProfileComplete(): boolean {
    const profile = this.getProfile();
    const hasEmail = !!(profile?.email?.trim() || profile?.jiraEmail?.trim());
    return !!(profile && profile.name && hasEmail && profile.jiraToken && profile.jiraProject);
  }

  /**
   * Get profile field
   */
  static getField(field: keyof ProfileData): string | undefined {
    const profile = this.getProfile();
    return profile ? profile[field] : undefined;
  }

  /**
   * Update profile field
   */
  static updateField(field: keyof ProfileData, value: string): boolean {
    const profile = this.getProfile();
    if (!profile) return false;

    profile[field] = value;
    return this.saveProfile(profile);
  }
}
